<template>
  <div class="memberSummary">
    <v-form v-model="isFormValid" ref="form" lazy-validation>
      <v-row class="mt-2 mb-4">
        <v-col cols="4" :style="{ maxWidth: formMaxWidth + 'px' }">
          <DatePicker v-model="investDate" label="Invest date" :rules="rules" />
        </v-col>
      </v-row>
    </v-form>
    <div v-if="loading">
      <v-progress-circular
        :size="20"
        color="primary"
        class="mr-2"
        indeterminate
      ></v-progress-circular>
      Loading...
    </div>
    <div class="memberCharts">
      <v-data-table
        dense
        :headers="investHeaders"
        :items="memberSummary"
        item-key="PortfolioId"
        :items-per-page="-1"
      >
        <template
          v-for="header in investHeaders.filter((header) => header.format !== '')"
          v-slot:[`item.${header.value}`]="{ item, value }"
        >
          {{ formatTableEl(item, value, header.format) }}
        </template>
        <template v-if="memberSummary.length" slot="body.append">
          <tr>
            <td><strong>Investment totals</strong></td>
            <td></td>
            <td></td>
            <td :style="{ textAlign: 'right' }">
              <strong>{{ valueTotals }}</strong>
            </td>
            <td :style="{ textAlign: 'right' }">
              <strong>{{ contsTotals }}</strong>
            </td>
            <td></td>
          </tr>
        </template>
      </v-data-table>
      <div v-if="assetOptions && investOptions">
        <v-row>
          <v-col cols="6" xl="4">
            <b>By Asset</b>
          </v-col>
          <v-col cols="6" xl="4">
            <b>By Fund</b>
          </v-col>
        </v-row>
        <v-row class="pb-6">
          <v-col cols="6" xl="4">
            <apexchart
              type="pie"
              width="500"
              :options="assetOptions"
              :series="assetOptions.series"
              class="mr-10"
            ></apexchart>
          </v-col>
          <v-col cols="6" xl="4">
            <apexchart
              type="pie"
              width="500"
              :options="investOptions"
              :series="investOptions.series"
            >
            </apexchart>
          </v-col>
        </v-row>
      </div>
    </div>

    <v-footer padless fixed outlined class="pl-5 pr-5 pt-3 pb-3" color="white" elevation="3">
      <v-spacer></v-spacer>
      <v-btn depressed color="primary" @click="onGo"> Go </v-btn>
    </v-footer>
  </div>
</template>

<script>
import DatePicker from '../../components/formControls/DatePicker.vue';
import Utils from '@/utils';

export default {
  name: 'MemberSummary',
  components: { DatePicker },

  data() {
    return {
      loading: false,
      rules: [(v) => !!v || 'Required.'],
      formMaxWidth: 500,

      investDate: new Date().toISOString(),
      memberSummary: [],

      isFormValid: false,

      investHeaders: [
        { text: 'Manager', value: 'ManagerName' },
        { text: 'Fund', value: 'FundName' },
        { text: 'Total units', value: 'Units', align: 'end', format: '{0:0.0000}' },
        { text: 'Total value', value: 'Value', align: 'end', format: '{0:0.00}' },
        { text: 'Total contributions', value: 'Contributions', align: 'end', format: '{0:0.00}' },
        { text: 'Price', value: 'BidPrice', align: 'end', format: '{0:0.0000}' },
      ],
    };
  },

  computed: {
    memberId() {
      return this.$store.getters.selectedMemberId;
    },

    valueTotals() {
      if (this.memberSummary.length) {
        const total = this.memberSummary.reduce((total, current) => (total += current.Value), 0);
        return this.formatTableEl(null, total, '{0:00.00}');
      }
      return null;
    },

    contsTotals() {
      if (this.memberSummary.length) {
        const total = this.memberSummary.reduce(
          (total, current) => (total += current.Contributions),
          0
        );
        return this.formatTableEl(null, total, '{0:00.00}');
      }
      return null;
    },

    assetOptions() {
      const labelsSet = new Set();
      const series = [];
      if (this.memberSummary.length) {
        this.memberSummary.forEach((s) => {
          labelsSet.add(s.AssetClass);
        });
        const labels = Array.from(labelsSet);
        console.log(labels);
        labels.forEach((label) => {
          series.push(
            this.memberSummary.reduce((total, current) => {
              if (current.AssetClass === label) {
                total += current.Value;
              }
              return Math.round(total * 100) / 100;
            }, 0)
          );
        });
        return { labels, series };
      }
      return null;
    },

    investOptions() {
      if (this.memberSummary.length) {
        const labels = this.memberSummary.map((s) => s.ManagerName + '/' + s.FundName);
        const series = this.memberSummary.map((s) => Math.round(s.Value * 100) / 100);

        return { labels, series };
      }
      return null;
    },
  },

  mounted() {
    if (!this.memberId) {
      this.$store.commit('setNotificationMessage', {
        type: 'info',
        text: 'Please, select member',
      });
      this.$router.push({ path: '/' });
    } else {
      this.$root.currentPage = 'Investments';
    }
  },

  destroyed() {
    this.$root.currentPage = null;
  },

  methods: {
    formatTableEl(item, value, format) {
      return Utils.formatTableValue(value, format);
    },

    onGo() {
      this.isFormValid = this.$refs.form.validate();
      if (this.isFormValid) this.getMemberSummary();
    },

    async getMemberSummary() {
      this.loading = true;
      try {
        const response = await this.$api.get(
          `/Summary/GetMemberInvestmentSummary?membershipId=${this.memberId}&investDate=${this.investDate}`
        );
        this.memberSummary = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.memberSummary {
  margin-bottom: 20px;
  padding-left: 30px;
  padding-right: 30px;

  .v-data-table tbody tr {
    cursor: default;
  }
}
</style>
